/**
 * ============================================================================
 * useRecordCalibrationMutation — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/equipment/{id}/calibrations — record a new calibration.
 *          When `source` is INTERNAL the server requires a supervisor
 *          co-signature (`supervisor_signature_bundle_id`); EXTERNAL_VENDOR
 *          rows can carry just the technician bundle.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  CalibrationOutcome,
  CalibrationRecord,
  CalibrationSource,
} from '../EquipmentDetail/useEquipmentDetailQuery';

export interface RecordCalibrationInput {
  equipmentId: string;
  performed_at_utc: string;
  source: CalibrationSource;
  outcome: CalibrationOutcome;
  technician_signature_bundle_id: string;
  /** Required when source = INTERNAL. */
  supervisor_signature_bundle_id?: string;
  /** Required when source = EXTERNAL_VENDOR. */
  vendor_supplier_id?: string;
  /** When recording a corrective re-cal, the prior calibration id. */
  correction_of_calibration_id?: string;
  /** Free-form opaque payload — the server stores it verbatim. */
  measurements: Record<string, unknown>;
}

export function useRecordCalibrationMutation(): UseMutationResult<
  CalibrationRecord,
  Error,
  RecordCalibrationInput
> {
  return useMutation<CalibrationRecord, Error, RecordCalibrationInput>({
    mutationFn: ({ equipmentId, ...body }) =>
      apiFetch<CalibrationRecord>(
        `/api/v1/equipment/${encodeURIComponent(equipmentId)}/calibrations`,
        {
          method: 'POST',
          body,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
